import { buildTitle } from "@/lib/seo-quality";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { AdSlot } from "@/components/AdSlot";
import { FyndanuInline } from "@/components/FyndanuInline";
import { RelatedLinks } from "@/components/RelatedLinks";

type Faq = { q: string; a: string };
type Guide = {
  slug: string;
  title: string;
  description: string;
  keywords: string;
  updated: string;
  intro: string;
  sections: { h: string; body: string[] }[];
  faq: Faq[];
};

const GUIDES: Record<string, Guide> = {
  "vilket-korkort-kravs-for-husbil": {
    slug: "vilket-korkort-kravs-for-husbil",
    title: "Vilket körkort krävs för husbil?",
    description:
      "B, B96, BE, C1 eller C? Snabbguiden till vilket körkort som krävs för husbil baserat på totalvikt. Uppdaterad 2026.",
    keywords: "vilket körkort krävs för husbil, B96, C1 körkort, husbil totalvikt, körkort husbil",
    updated: "2026-07-03",
    intro:
      "Rätt körkort avgörs av husbilens totalvikt (angiven på registreringsbeviset), inte längd eller antal sängplatser. Här är den enkla översikten – och när du bör uppgradera.",
    sections: [
      {
        h: "B-körkort – upp till 3 500 kg totalvikt",
        body: [
          "Med vanligt B-körkort får du köra husbil vars totalvikt inte överstiger 3 500 kg. De flesta halvintegrerade husbilar på Fiat Ducato- eller Ford Transit-chassi ligger inom denna vikt.",
          "Tänk på: en husbil vägs full med vatten, gasol och passagerare. En ”3 500 kg-husbil” kan i praktiken lastas till >4 000 kg – då behöver du C1.",
        ],
      },
      {
        h: "B96 – husbil upp till 3 500 kg + släp",
        body: [
          "B96 tas via kompletterande utbildning (inget nytt prov). Ger dig rätt att dra släp där ekipagets totalvikt är max 4 250 kg. Praktiskt om du drar liten släpvagn med MC eller cyklar.",
        ],
      },
      {
        h: "BE – större släp bakom husbilen",
        body: [
          "BE-körkortet kräver eget prov och tillåter släp upp till 3 500 kg bakom en bil eller husbil ≤3 500 kg. För tyngre kombinationer behöver du C1E eller CE.",
        ],
      },
      {
        h: "C1 – husbil 3 500–7 500 kg",
        body: [
          "Så snart husbilens totalvikt överstiger 3 500 kg krävs C1. Detta gäller många integrerade husbilar och alcove-modeller. C1 kräver läkarintyg, teoriprov och uppkörning.",
          "Är du osäker på om din husbil kräver C1? Titta på fält F.1 (tekniskt tillåten totalvikt) och F.2 (registrerad totalvikt) i registreringsbeviset – det är F.2 som styr.",
        ],
      },
      {
        h: "C – husbil över 7 500 kg",
        body: [
          "Ovanligt bland privatpersoner, men lyxbussar och exklusiva liners kan väga mer och kräver då fullt C-behörighet.",
        ],
      },
      {
        h: "Undantag för äldre B-körkort (före 1996)",
        body: [
          "Har du tagit B-körkort före 1 juli 1996 har du automatiskt utökad behörighet motsvarande C1 (upp till 7 500 kg) för husbil. Kontrollera på Transportstyrelsens Mina sidor.",
        ],
      },
    ],
    faq: [
      {
        q: "Vilket körkort behöver jag för en husbil på 3 500 kg?",
        a: "B-körkort räcker om totalvikten är exakt 3 500 kg eller lägre. Överstigs vikten även med 1 kg krävs C1.",
      },
      {
        q: "Får jag köra husbil på 4 500 kg med B-körkort?",
        a: "Nej, då krävs C1 (eller utökad B för dig som tog körkort före 1 juli 1996).",
      },
      {
        q: "Vad är skillnaden på B och B96?",
        a: "B ger dig rätt att köra husbil upp till 3 500 kg. B96 ger dessutom rätt att dra släp där ekipagets totalvikt är upp till 4 250 kg.",
      },
      {
        q: "Räknas husbilen som lastbil?",
        a: "Ja, en husbil över 3 500 kg registreras som ”tung lastbil personbil klass II” och kräver C1 eller C.",
      },
    ],
  },
  "husbil-vinterforvaring": {
    slug: "husbil-vinterforvaring",
    title: "Vinterförvaring av husbil – komplett checklista",
    description:
      "Så vinterförvarar du husbilen rätt: vatten, batteri, gasol, tätningar och däck. Ärlig checklista från en handlare i Norrbotten.",
    keywords: "husbil vinterförvaring, vinterförvara husbil, avställa husbil, husbil vinter",
    updated: "2026-07-03",
    intro:
      "Fel förvaring vintertid är den vanligaste orsaken vi ser till dyra reparationer på våren. Med en timmes förberedelse sparar du tusenlappar – och undviker frostsprängda rör.",
    sections: [
      {
        h: "1. Töm hela vattensystemet",
        body: [
          "Öppna alla kranar (varm + kall), töm färskvattentank, spolarvätsketank för toalett, samt gråvattentank. Aktivera säkerhets-/frostventilen på varmvattenberedaren – lämna den öppen hela vintern.",
          "Kör vattenpumpen tills luft blåser genom kranarna. Blås ur duschslangen. Kvarvarande vatten i rören är den vanligaste frostskadan.",
        ],
      },
      {
        h: "2. Skydda batterierna",
        body: [
          "Boendebatteri: ladda till 100 %, koppla ur eller anslut smart underhållsladdare. Litiumbatterier vill förvaras vid ~50 % laddning i sval miljö.",
          "Startbatteri: koppla loss minuskabeln eller använd underhållsladdning. Ett urladdat blybatteri fryser vid −10 °C och blir förstört.",
        ],
      },
      {
        h: "3. Toalett och avlopp",
        body: [
          "Töm och skölj kassett samt spolvattentank. Låt locken stå på glänt så tätningarna får luft. Smörj gummitätningar med silikonspray – annars torkar de och läcker till våren.",
        ],
      },
      {
        h: "4. Gasol",
        body: [
          "Stäng huvudkran vid flaskan. Kontrollera bäst-före-datum på gasolslangar (10 år). Vid utomhusförvaring: förvara flaskorna på anvisad plats – aldrig i husbilen om den står inomhus.",
        ],
      },
      {
        h: "5. Däck och chassi",
        body: [
          "Pumpa däcken till max angivet tryck – annars får du platta punkter. Ännu bättre: ställ husbilen på pallbockar så däcken avlastas helt.",
          "Släpp handbromsen (klossa hjulen istället) – annars kan bromsen rosta fast.",
        ],
      },
      {
        h: "6. Ventilation och fukt",
        body: [
          "Öppna alla skåpsluckor, madrasser på högkant, lådor lite utdragna. Ställ in en absorbenthink (t.ex. Airdry) i boendedelen. Fukt är husbilens värsta fiende vintertid.",
          "Takventiler i öppet läge (om under tak) – annars stängda men kolla att tätningen är hel.",
        ],
      },
      {
        h: "7. Efter vintern – snabb genomgång innan första turen",
        body: [
          "Fyll färskvattentank + kör igenom systemet med desinfektionsmedel. Kontrollera däcktryck. Prova gasol med läcksökspray. Titta över alla takfogar innan säsong.",
        ],
      },
    ],
    faq: [
      {
        q: "Måste jag ställa av husbilen på vintern?",
        a: "Nej, men det sparar skatt och försäkring. Kom ihåg att en avställd husbil inte får framföras på allmän väg.",
      },
      {
        q: "Kan jag förvara husbilen utomhus?",
        a: "Ja, om vattensystemet är fullständigt tömt och taket är fritt från snöansamling. Kontrollera efter varje storm.",
      },
      {
        q: "Vad händer om jag glömmer att tömma vattnet?",
        a: "Rör, pumpar och varmvattenberedare kan spricka – en typisk reparation kostar 8 000–20 000 kr.",
      },
    ],
  },
  "kopa-husbil-checklista": {
    slug: "kopa-husbil-checklista",
    title: "Köpa husbil – 20-punkters checklista före affären",
    description:
      "Ska du köpa husbil? Här är checklistan vi själva använder: fukt, chassi, gasol, batteri, historik. Från AK Husbil & Fritid i Kalix.",
    keywords: "köpa husbil, begagnad husbil checklista, husbil att tänka på, husbilsköp",
    updated: "2026-07-03",
    intro:
      "En husbil är två fordon i ett – ett chassi och ett hus. Båda kan dölja problem som kostar tiotusentals kronor. Använd checklistan och våga fråga.",
    sections: [
      {
        h: "Papper & historik (1–5)",
        body: [
          "1. Registreringsbevis: kontrollera totalvikt (F.2), servicevikt och tillåten släpvikt.",
          "2. Servicehistorik: fullständig hos märkesverkstad är plus.",
          "3. Antal ägare och mil – ovanligt låg mil för åldern kan vara varningssignal (stått stilla = fuktrisk).",
          "4. Besiktningsprotokoll senaste 3 åren.",
          "5. Kontrollera hos Transportstyrelsen om husbilen är belånad eller har körförbud.",
        ],
      },
      {
        h: "Kaross & fukt (6–10)",
        body: [
          "6. Fuktmätning ska ha utförts inom senaste 12 månader – begär protokoll. Värden över 15 % är varningssignal.",
          "7. Titta i alla hörn, under sängar och i garaget efter missfärgningar och bubbliga tapeter.",
          "8. Kontrollera alla takfogar utifrån – silikon som spricker = renovering.",
          "9. Öppna alla luckor och fönster – kolla tätningsgummin.",
          "10. Böj lätt på golvet vid dörr och badrum – det får inte kännas mjukt.",
        ],
      },
      {
        h: "Teknik & system (11–16)",
        body: [
          "11. Gasol: läcksök vid alla anslutningar. Systemet ska ha OK-besiktning (max 4 år gammal).",
          "12. Värme (Truma/Alde): starta kallt, ska tända inom 60 sek.",
          "13. Vattensystem: fyll och kör pumpen. Lyssna efter läckljud, kolla under sängar.",
          "14. Kyl: prova både 230V, 12V och gasol.",
          "15. Toalett: kassetten ska glida lätt. Spolpump ska ge fullt tryck.",
          "16. Solpanel och batteribank: mät spänning belastad och obelastad.",
        ],
      },
      {
        h: "Chassi & provkörning (17–20)",
        body: [
          "17. Provkör minst 30 min inklusive motorväg. Lyssna efter drivknutar, växellåda, bromsvibrationer.",
          "18. Kontrollera däckens tillverkningsår (DOT-kod) – >6 år bör bytas oavsett mönster.",
          "19. Rost under chassit – särskilt runt bakre fjäderfästen.",
          "20. Testa alla elförbrukare: markis, TV, förtältslampa, USB-uttag.",
        ],
      },
    ],
    faq: [
      {
        q: "Är begagnad husbil ett bra köp?",
        a: "Ja, om den är fuktmätt och genomgången. En 5–8 år gammal husbil har tappat merparten av värdeminskningen och kan hålla i 15 år till med rätt skötsel.",
      },
      {
        q: "Hur mycket ska jag räkna med utöver köpesumman?",
        a: "Räkna med minst 15 000 kr första året: besiktning, försäkring, service, tillbehör och ev. mindre åtgärder.",
      },
      {
        q: "Vad är det viktigaste att kontrollera?",
        a: "Fukt. Ett fuktskadat golv eller vägg kan lätt kosta 30 000–100 000 kr att åtgärda.",
      },
    ],
  },
  "vardera-husbil-husvagn": {
    slug: "vardera-husbil-husvagn",
    title: "Värdera husbil & husvagn – så räknar du ut rätt pris",
    description:
      "Så värderar du husbil och husvagn 2026: marknadsprisjämförelse, avdrag för fukt och slitage, samt vilka papper som höjer värdet.",
    keywords:
      "husvagnsvärdering, värdera husbil, värdera husvagn, husbil värde, vad är min husvagn värd, husbilsvärdering",
    updated: "2026-08-04",
    intro:
      "Det finns ingen officiell prislista för husbilar och husvagnar – värdet sätts av vad liknande exemplar faktiskt säljs för, minus konkreta avdrag för fukt, slitage och saknad historik. Vi värderar fritidsfordon varje vecka i Kalix, och nedan är exakt samma arbetsgång vi själva använder.",
    sections: [
      {
        h: "1. Börja med utgångspriset: vad säljs liknande exemplar för?",
        body: [
          "Sök upp 8–12 annonser på samma modell, samma planlösning och årsmodell ±2 år. Använd flera marknadsplatser – Fyndanu.se, Blocket och handlarlager – så du ser hela spannet, inte bara de mest optimistiskt prissatta annonserna.",
          "Stryk högsta och lägsta priset och räkna medianen på resten. Det är ditt utgångspris. Kom ihåg att annonspriser är begärda priser: en privataffär landar normalt 5–10 % under annonspris, en inbytesvärdering hos handlare 15–25 % under, eftersom handlaren tar risken för fukt, garanti och genomgång.",
          "Titta också på hur länge annonserna legat kvar. Ligger samma modell osåld i tre månader är marknadspriset lägre än vad annonserna påstår.",
        ],
      },
      {
        h: "2. Justera för mätbara faktorer – inte känslor",
        body: [
          "Miltal på husbil: normalt är 8 000–12 000 km per år. Ligger fordonet klart över drar du av; klart under lägger du till – men bara om servicehistoriken finns, för en lågmilare som stått still har ofta torra tätningar och gamla däck.",
          "Däck äldre än sex år (DOT-koden på sidan) är en kostnad på 8 000–15 000 kr för fyra däck på husbil. Batteribyte 4 000–12 000 kr beroende på om det är blysyra eller litium. Ny kylskåpsservice eller gasolbesiktning några tusen. Dra av det som återstår att göra.",
          "Utrustning som faktiskt påverkar priset: solceller, växelriktare, litiumbatteri, dieselvärmare, markis, backkamera, cykelhållare och vinterpaket. Inredningsdetaljer, textilier och kosmetiska tillbehör påverkar knappt alls.",
        ],
      },
      {
        h: "3. Fukten avgör mer än allt annat",
        body: [
          "En aktuell fuktmätning är den viktigaste riskuppgiften i värderingen. Ett dokumenterat problem påverkar priset både genom reparationskostnaden och genom köparens osäkerhet.",
          "Använd vår fullständiga fukttestguide för gränsvärden, tolv mätpunkter, kostnad per skadestadium och när verkstad krävs: /fukttest-husbil.",
        ],
      },
      {
        h: "4. Papper och historik som höjer värdet",
        body: [
          "Komplett servicebok med stämplar, fuktprotokoll varje år, gasolbesiktning, kvitton på reparationer och tillbehör, samt originalnycklar och manualer. Var för sig små saker – tillsammans skillnaden mellan snabb affär till fullt pris och en utdragen förhandling.",
          "Saknas historiken helt bör du räkna av 10–15 %, för då måste köparen prisa in okända risker.",
          "Har du kvar originalannonsen eller specifikationen från leveransen är den guld värd när du ska visa vilken utrustningsnivå fordonet faktiskt har – många modeller såldes i tre eller fyra olika paket.",
        ],
      },
      {
        h: "5. Säsong och läge påverkar mer än man tror",
        body: [
          "Efterfrågan toppar mars–juni. Säljer du i november får du ofta 5–10 % mindre för exakt samma fordon. Vill du maximera priset: gör fuktmätning och service under vintern och lägg ut annonsen i mars.",
          "Geografiskt spelar det roll också. Vinteranpassade husbilar med dieselvärmare och isolerade tankar betalas bättre i Norrbotten och Västerbotten än i Skåne, medan lätta van-modeller går snabbare i storstadsområdena.",
        ],
      },
      {
        h: "Räkneexempel: husvagn 2016, 1 400 kg",
        body: [
          "Median på tio jämförbara annonser: 149 000 kr. Justering för privatförsäljning: −7 % → cirka 139 000 kr.",
          "Avdrag: däck från 2016 (−6 000 kr), kylskåpsservice ej gjord (−3 000 kr), fuktmätning 17 % i bakvägg (−25 000 kr för åtgärd). Tillägg: markis och 100 W solcell (+6 000 kr), komplett servicehistorik (+8 000 kr).",
          "Realistiskt marknadsvärde: cirka 119 000 kr vid privatförsäljning, cirka 95 000–100 000 kr som inbyte hos handlare. Skillnaden är handlarens risk och genomgång – inte ett dåligt bud.",
        ],
      },
      {
        h: "Vill du ha en riktig värdering?",
        body: [
          "Vi köper husbilar och husvagnar och gör värderingar på plats i Kalix. Du får ett konkret bud, inte ett intervall – och vi berättar vad vi baserar det på: fuktmätning, gasolkontroll, teknisk genomgång och aktuella marknadspriser.",
          "Ring 070-862 97 43 eller skicka registreringsnummer, årsmodell, miltal och några bilder via kontaktformuläret, så återkommer vi med en bedömning.",
        ],
      },
    ],
    faq: [
      {
        q: "Hur värderar jag min husvagn själv?",
        a: "Ta medianen av 8–12 aktuella annonser på samma modell och årsmodell, dra av 5–10 % för att annonspriser är begärda priser, och justera sedan för däckålder, servicebehov, utrustning och fuktmätning.",
      },
      {
        q: "Finns det någon officiell prislista för husbilar?",
        a: "Nej. Till skillnad från personbilar finns ingen officiell värderingstabell för husbilar och husvagnar. Marknadspriser i aktuella annonser plus fuktstatus är det som styr värdet.",
      },
      {
        q: "Hur mycket sjunker värdet av en fuktskada?",
        a: "Normalt 30–50 % av det torra värdet, eftersom en golv- eller vägglagning kostar 30 000–100 000 kr och tar fordonet ur bruk i veckor.",
      },
      {
        q: "Varför är inbytesvärdet lägre än annonspriset?",
        a: "En handlare tar över risken för fukt och dolda fel, gör teknisk genomgång, lämnar garanti och binder kapital under försäljningstiden. Det är normalt 15–25 % under annonspris.",
      },
      {
        q: "När på året får jag mest för husbilen?",
        a: "Mars till juni. Efterfrågan är då högst och samma fordon betalas ofta 5–10 % bättre än under november–januari.",
      },
      {
        q: "Höjer solceller värdet?",
        a: "Ja, fast installerade solceller, litiumbatteri och dieselvärmare är bland få tillbehör som ger tydligt genomslag i pris. Textilier och lösa tillbehör påverkar knappt.",
      },
    ],
  },
};

export const Route = createFileRoute("/guider/$slug")({
  loader: ({ params }) => {
    const guide = GUIDES[params.slug];
    if (!guide) throw notFound();
    return { guide };
  },
  head: ({ loaderData, params }) => {
    const g = loaderData?.guide;
    if (!g) {
      return { meta: [{ title: "Guide hittades inte" }, { name: "robots", content: "noindex" }] };
    }
    const url = `https://www.akhusbilfritid.se/guider/${params.slug}`;
    return {
      meta: [
        { title: buildTitle(g.title) },
        { name: "description", content: g.description },
        { name: "keywords", content: g.keywords },
        { property: "og:type", content: "article" },
        { property: "og:title", content: g.title },
        { property: "og:description", content: g.description },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: g.title,
            description: g.description,
            datePublished: g.updated,
            dateModified: g.updated,
            inLanguage: "sv-SE",
            author: { "@type": "Organization", name: "AK Husbil & Fritid", url: "https://www.akhusbilfritid.se" },
            publisher: {
              "@type": "Organization",
              name: "AK Husbil & Fritid",
              logo: { "@type": "ImageObject", url: "https://www.akhusbilfritid.se/og-image.jpg" },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: g.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
              { "@type": "ListItem", position: 2, name: "Guider", item: "https://www.akhusbilfritid.se/guider" },
              { "@type": "ListItem", position: 3, name: g.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: GuidePage,
  notFoundComponent: () => (
    <div className="container-edge py-32 text-center">
      <h1 className="font-display text-5xl">Guiden finns <span className="font-italic-display text-accent">inte</span>.</h1>
      <Link to="/guider" className="mt-8 inline-flex rounded-full bg-primary px-5 py-3 text-sm text-primary-foreground">Till guider</Link>
    </div>
  ),
});

function GuidePage() {
  const { guide } = Route.useLoaderData();
  return (
    <article className="container-edge max-w-3xl py-16 md:py-24">
      <Link to="/guider" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent">
        <ArrowLeft size={14} /> Till guider
      </Link>
      <p className="mt-10 text-xs font-medium uppercase tracking-wider text-accent">
        Uppdaterad{" "}
        <time dateTime={guide.updated}>
          {new Date(guide.updated).toLocaleDateString("sv-SE", { year: "numeric", month: "long", day: "numeric" })}
        </time>
      </p>
      <h1 className="mt-4 font-display text-5xl leading-[1.02] md:text-6xl">{guide.title}</h1>
      <p className="mt-6 text-xl leading-relaxed text-muted-foreground">{guide.intro}</p>

      <div className="mt-12 space-y-12">
        {guide.sections.map((s: Guide["sections"][number], si: number) => (
          <section key={s.h}>
            <h2 className="font-display text-3xl leading-tight md:text-4xl">{s.h}</h2>
            <div className="mt-4 space-y-4">
              {s.body.map((p: string, i: number) => (
                p.endsWith("/fukttest-husbil.") ? (
                  <p key={i} className="leading-relaxed text-foreground/85">
                    Läs hela genomgången i vår{" "}
                    <Link to="/fukttest-husbil" className="font-medium text-accent underline underline-offset-4">
                      guide till fukttest, gränsvärden och reparationskostnader
                    </Link>
                    .
                  </p>
                ) : (
                  <p key={i} className="leading-relaxed text-foreground/85">{p}</p>
                )
              ))}
            </div>
            {si === Math.min(1, guide.sections.length - 1) && (
              <FyndanuInline
                context={`${guide.title} ${guide.intro}`}
                className="mt-10"
              />
            )}
            {guide.sections.length > 4 && si === Math.floor(guide.sections.length / 2) && (
              <AdSlot variant="in-article-2" />
            )}
          </section>
        ))}
      </div>

      <AdSlot variant="in-article" />

      <section className="mt-16 rounded-3xl border border-border bg-card p-8">
        <h2 className="font-display text-3xl">Vanliga frågor</h2>
        <dl className="mt-6 space-y-6">
          {guide.faq.map((f: Faq) => (
            <div key={f.q}>
              <dt className="font-display text-xl">{f.q}</dt>
              <dd className="mt-2 text-foreground/85">{f.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <RelatedLinks
        keyId={guide.slug ?? guide.title}
        text={`${guide.title} ${guide.intro} ${guide.sections.map((s: Guide["sections"][number]) => `${s.h} ${s.body.join(" ")}`).join(" ")}`}
        currentHref={`/guider/${guide.slug ?? ""}`}
      />

      <AdSlot variant="multiplex" label="Fler artiklar" />



      <aside className="mt-12 rounded-3xl border border-accent/30 bg-accent/5 p-8">
        <p className="eyebrow">Behöver du prata med någon?</p>
        <h3 className="mt-3 font-display text-3xl">Ring Aaron på <span className="font-italic-display text-accent">070-862 97 43</span></h3>
        <p className="mt-3 text-sm text-muted-foreground">Vi svarar gärna på frågor även om du inte köper av oss. Familjeägt i Kalix sedan 2004.</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/fordon" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            Se aktuella fordon <ArrowUpRight size={14} />
          </Link>
          <Link to="/kontakt" className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-5 py-3 text-sm font-medium hover:bg-secondary">
            Kontakta oss
          </Link>
        </div>
      </aside>
    </article>
  );
}
